/**
 * Multi-week availability — calls `useCommonAvailability` at a
 * fixed set of week offsets and returns the merged `Interval[]`.
 *
 * Why: the self-schedule pipeline needs availability for every
 * week the user might have auto-filled (Find Time "next 2 weeks"
 * spans the visible week + the two after it). The single-week
 * `useCommonAvailability` only covers the visible anchor — counts
 * for future weeks come back zero until the calendar navigates.
 *
 * How: React's rules-of-hooks forbid calling a hook in a loop, so
 * we call `useCommonAvailability` a fixed `WEEKS_AHEAD` times at
 * compile-time-known offsets. React Query de-dupes the fetches
 * across the calendar so the extra calls are cheap after the
 * first paint.
 *
 * Tune `WEEKS_AHEAD` if product wants longer Find Time windows.
 */
import { useMemo } from 'react';
import moment from 'moment-timezone';
import type { InterviewerScheduleByUserId } from '@/api/interviewerSchedule/types';
import type { Interval } from '../../availability';
import {
  useCommonAvailability,
  type UseCommonAvailabilityArgs,
} from './useCommonAvailability';

// ─────────────────────────────────────────────────────────────────
// Tuning constants.
// ─────────────────────────────────────────────────────────────────

/**
 * Number of consecutive weeks we fetch, anchored at the caller's
 * `weekStartIso`. 4 covers the widest Find Time mode ("next 2
 * weeks") plus a buffer.
 */
const WEEKS_AHEAD = 4;

// ─────────────────────────────────────────────────────────────────
// Public API.
// ─────────────────────────────────────────────────────────────────

export interface UseCommonAvailabilityRangeArgs extends Omit<
  UseCommonAvailabilityArgs,
  'weekStartIso'
> {
  /** Anchor for the first fetched week. */
  weekStartIso: string;
}

export interface UseCommonAvailabilityRangeResult {
  /** Merged availability across all `WEEKS_AHEAD` weeks. */
  availability: Interval[];
  /** `true` while any of the N fetches is still resolving. */
  isLoading: boolean;
  /**
   * Raw per-interviewer working-hours map merged across all
   * `WEEKS_AHEAD` weeks. Same key shape as the single-week hook;
   * values concatenate the per-week intervals so the same
   * `interviewerId` accumulates the full window. Used by payload-time
   * override classification — see `payload/computeSlotOverride.ts`.
   */
  scheduleByUserId: InterviewerScheduleByUserId;
}

/** Shift a week-anchor ISO by `n` weeks. */
function shiftWeek(weekStartIso: string, timezone: string, n: number): string {
  return moment.tz(weekStartIso, timezone).add(n, 'weeks').toISOString();
}

export function useCommonAvailabilityRange({
  weekStartIso,
  timezone,
  interviewerIds,
  interviewKind,
  enabled = true,
}: UseCommonAvailabilityRangeArgs): UseCommonAvailabilityRangeResult {
  // Fixed 4 calls. Each targets `weekStartIso + i weeks`. React's
  // rules-of-hooks are satisfied because the call count is a
  // compile-time constant.
  const common = {
    timezone,
    interviewerIds,
    interviewKind,
    enabled,
  } satisfies Omit<UseCommonAvailabilityArgs, 'weekStartIso'>;
  const weekA = useCommonAvailability({
    weekStartIso: shiftWeek(weekStartIso, timezone, 0),
    ...common,
  });
  const weekB = useCommonAvailability({
    weekStartIso: shiftWeek(weekStartIso, timezone, 1),
    ...common,
  });
  const weekC = useCommonAvailability({
    weekStartIso: shiftWeek(weekStartIso, timezone, 2),
    ...common,
  });
  const weekD = useCommonAvailability({
    weekStartIso: shiftWeek(weekStartIso, timezone, 3),
    ...common,
  });

  const availability = useMemo<Interval[]>(
    () => [
      ...weekA.availability,
      ...weekB.availability,
      ...weekC.availability,
      ...weekD.availability,
    ],
    [
      weekA.availability,
      weekB.availability,
      weekC.availability,
      weekD.availability,
    ]
  );

  const scheduleByUserId = useMemo<InterviewerScheduleByUserId>(() => {
    // Concatenate per-user intervals across all weeks. Same id might
    // be present in multiple week maps (one entry per fetched week);
    // we flatten, the `computeSlotOverride` consumer treats the union
    // of intervals as the user's full schedule for the window covered.
    const merged: InterviewerScheduleByUserId = {};
    const merge = (chunk: InterviewerScheduleByUserId) => {
      for (const [id, intervals] of Object.entries(chunk)) {
        if (!intervals || intervals.length === 0) continue;
        const existing = merged[id];
        merged[id] = existing ? [...existing, ...intervals] : intervals;
      }
    };
    merge(weekA.scheduleByUserId);
    merge(weekB.scheduleByUserId);
    merge(weekC.scheduleByUserId);
    merge(weekD.scheduleByUserId);
    return merged;
  }, [
    weekA.scheduleByUserId,
    weekB.scheduleByUserId,
    weekC.scheduleByUserId,
    weekD.scheduleByUserId,
  ]);

  const isLoading =
    weekA.isLoading || weekB.isLoading || weekC.isLoading || weekD.isLoading;

  return { availability, isLoading, scheduleByUserId };
}

// Exported for callers that need to know how many weeks this
// covers — e.g., the Find Time walker when clamping its N-week
// mode to what the pipeline can actually see.
export const COMMON_AVAILABILITY_RANGE_WEEKS = WEEKS_AHEAD;
